/*eslint no-console:1 */
/* global Browser */

//import Promise from 'promise-polyfill';
import { debounce, toArray } from 'lodash';
import { addListener, removeListener, trigger } from '../lib/event';
import { isChildNode } from '../lib/helper';

const listElem = 'li';
const activeClass = 'active';

const upArrow = 38;
const downArrow = 40;
const enter = 13;
const escape = 27;
const tab = 9;

/**
 * This is the Base Class of the autocomplete (typeahead like) Components
 * You have to extend this Class and implement the getResult method for it to work
 *
 */
export default class AutocompleteBase {

  constructor(el) {

    this.el = el;
    this.value = el.value;
    this.index = -1;

    this.suggestionList = document.querySelector(el.getAttribute('data-autocomplete'));

    if (!this.suggestionList) {

      throw new Error('[AutocompleteBase] No suggestion-list was found.');
    }

    this.el.setAttribute('autocomplete', 'off');
    this.isFilter = this.suggestionList.classList.contains('filter-results');

    this._render = debounce(this.render.bind(this));
    this._input = debounce(this.onInput.bind(this), 500);
    this._click = this.onClick.bind(this);
    this._hover = this.onHover.bind(this);
    this._prevent = preventKeys(enter, upArrow, downArrow);
    this._hide = this.hide.bind(this);
    this._checkHide = this.checkHide.bind(this);

    this.resizeHandler = this.hide.bind(this);

    // handle input
    this.el.addEventListener('keydown', this._input);

    // the search form
    this.search = document.getElementById('search');

    // handle blur
    // this.el.addEventListener('blur', this.resizeHandler);

    if (!this.isFilter) {
      // custom event
      window.addEventListener('headerChange', this.resizeHandler);
    }

    // disables some keys
    this.el.addEventListener('keyup', this._prevent);
    this.el.addEventListener('keypress', this._prevent);
    this.el.addEventListener('keydown', this._prevent);

    this.suggestionList.addEventListener('click', this._click);
    this.suggestionList.addEventListener('keyup', this.onKeyUpList.bind(this));
    // this.suggestionList.addEventListener('touchend', this._click);
    this.suggestionList.addEventListener('mouseover', this._hover);

    window.addEventListener('keyup', this._checkHide);
    window.addEventListener('click', this._checkHide);
  }

  destroy() {

    // custom event
    window.removeEventListener('headerChange', this.resizeHandler);

    // disables some keys
    this.el.removeEventListener('keydown', this._input);
    this.el.removeEventListener('keyup', this._prevent);
    this.el.removeEventListener('keypress', this._prevent);
    this.el.removeEventListener('keydown', this._prevent);

    this.suggestionList.removeEventListener('click', this._click);
    this.suggestionList.removeEventListener('keyup', this.onKeyUpList.bind(this));
    this.suggestionList.removeEventListener('mouseover', this._hover);

    window.removeEventListener('keyup', this._checkHide);
    window.removeEventListener('click', this._checkHide);
  }

  /**
   * Get the results and returns a promise
   * the result should have {html, value} properties
   * @param input the value of the input field
   * @override
   */
  // getResults(input) {

  //   // dummy results
  //   // return Promise.resolve([{value: input, html: input}]);
  // }

  /**
   * will be called any time the user selects a value
   * @param result
   * @override
   */
  onSelect(result) {

    if (this.isFilter) {

      trigger(this.el, 'onFilterSet');
    } else {

      window.location = result.url || result.filterUrl;
    }
  }

  onInput(e) {
    const which = e.which;

    if (which === upArrow) {

      return this.activate(e, this.index - 1);
    }

    if (which === downArrow) {

      return this.activate(e, this.index + 1);
    }

    if (which === enter) {

      if (this.index > -1) {

        return this.select(this.index);
      } else {

        //search directly!
        if (this.el.getAttribute('id') === 'search-term') {

          return this.search.submit();
        } else {

          return false;
        }
      }

    }

    if (this.el.value === this.value) {

      return;
    }

    this.value = this.el.value;

    this._render.cancel();
    this.getResults(this.el.value)
      .then(this._render)
      .catch(error => {
        throw new Error(error);
        // console.log('[AUTOCOMPLETE] rendering error');
      });
  }

  onKeyUpList(e) {

    if (e.keyCode && (e.keyCode === 13 || e.keyCode === 32)) {

      this._click(e);
    }
  }

  onClick(e) {

    const li = e.target.closest(listElem);

    if (!li) {

      return;
    }

    this.select(parseInt(li.getAttribute('data-index')));
  }

  onHover(e) {

    this.activateLi(e, e.target.closest(listElem));
  }

  activate(e, index) {

    if (!this.results) {

      return;
    }

    let filterLength = 0;
    let li = null;

    index = Math.max(index, 0);

    if (this.results.filters) {

      filterLength = this.results.filters.length;
    }

    index = Math.min(index, filterLength + this.results.results.length - 1);

    this.index = index;

    li = this.suggestionList.querySelectorAll(listElem)[index];

    this.suggestionList.scrollTop = li.offsetTop;

    this.activateLi(e, li);
  }

  activateLi(e, li) {

    if (!li) {
      return;
    }

    toArray(this.suggestionList.querySelectorAll(listElem)).forEach(el => el.classList.remove(activeClass));
    li.classList.add(activeClass);
  }

  select(index) {

    let result = null;
    const filterLength = (this.results.filters) ? this.results.filters.length : 0;


    if (index < filterLength) {

      if (index > -1) {

        result = this.results.filters[index];
      } else {

        return false;
      }

    } else {

      result = this.results.results[index - filterLength];
    }

    if (result.displayName) {

      this.el.value = this.value = result.displayName;
    }

    this.onSelect(result);
    this.hide();
  }

  render(results) {

    this.results = results;
    this.index = -1;

    this.suggestionList.innerHTML = template(results, this.value, this);
    this.show();
  }

  show() {

    let styleString = '';

    if (this.isFilter && this.suggestionList.firstChild !== null) {

      styleString = 'display: block';

      if (Browser.browserName === 'Firefox') {

        styleString += '; visibility: visible';
      }
    } else {

      const dimensions = document.getElementById('search-term').getBoundingClientRect();
      styleString = `width:${dimensions.width}px; display: block`;
    }

    this.suggestionList.setAttribute('style', styleString);

    addListener(window, 'resize', this.resizeHandler);
  }

  hide(e = null) {

    // TODO: handle blur differently -
    // if (e.type === 'blur') {
    //
    //   console.log('e', e);
    // }

    removeListener(window, 'resize', this.resizeHandler);

    this.suggestionList.style.display = 'none';
  }

  checkHide(e) {

    if (
      e.keyCode && (e.keyCode === escape) ||
      (!e.keyCode || e.keyCode && e.keyCode === tab) && e.target !== this.el && !isChildNode(e.target, this.el.parentNode)
    ) {

      this.hide();
    }

    if (this.suggestionList.style.display === 'block' && this.suggestionList.firstChild !== null) {

      if (this.suggestionList.firstChild.childNodes.length === 0) {

        this.hide();
      }
    }
  }
};

// TODO: move to class and override in Autocomplete
const template = (data, inputValue, that) => {

  // TODO: can both be empty? what do we do then?

  let section;
  let results;

  if (data.filters && data.filters.length) {

    section = `<ul class="is-section-result">${data.filters.map(({sectionDisplayName, filterUrl}, index) => `<li data-index="${index}"><a tabindex="-1" href="${filterUrl}">${data.term} <span>${sectionDisplayName}</span></a></li>`).join('')}</ul>`;

  } else {

    section = '';
  }

  if (data.results && data.results.length) {

    const resultsList = data.results.map(({displayName, url}, index) => {

      if ( that.isFilter ) {

        url = '#';
      }

      const resIndex = ( data.filters && data.filters.length ) ? index + data.filters.length : index;

      return `<li data-index="${resIndex}"><a tabindex="-1" href="${url}">${displayName.replace(new RegExp(inputValue, 'i'), '<b>$&</b>')}</a></li>`;
    }).join('');

    results = `<ul class="is-result-list">${resultsList}</ul>`;


  } else {

    results = '';
  }

  return `${section}${results}`;
};

function preventKeys(...keys) {

  return function (e) {
    
    keys.some(k => (k.charCode === 0) && (k === e.keyCode) && !e.preventDefault());
  };

}
